import { useSnackbarStack } from '@superdispatch/ui';
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useCarrierSettingsCache } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import {
  useCompleteInstantBankAccountVerification,
  useUploadMicroDepositScreenshot,
} from '../../data/MoovAPI';
import { trackSuperPayEvent } from '../../data/SuperPayAnalytics';
import { useSuperPaySettings } from '../../data/SuperPayAPI';
import {
  MvCodeOcrResult,
  readMvCodeFromImage,
} from './mv-code-ocr/readMvCodeFromImage';
import { getStatusErrors } from './getStatusErrors';

const SCREENSHOT_VERIFICATION_KEY = ['superpay', 'screenshot-verification'];

export type ScreenshotVerificationOutcome = 'verified' | 'submitted_for_review';

/** What Moov said about the code read from the screenshot. */
type AutoVerification =
  | 'verified'
  | 'rejected_incorrect'
  | 'rejected_exceeded'
  | 'error'
  | 'not_attempted';

interface ScreenshotVerificationResult {
  outcome: ScreenshotVerificationOutcome;
  autoVerification: AutoVerification;
  ocr: MvCodeOcrResult | null;
  durationMs: number;
}

interface ScreenshotVerificationVariables {
  file: File;
  onVerified?: () => void;
}

/**
 * With MV code OCR on, the check keeps running after the upload step closes.
 * With it off, the upload blocks the step as before.
 */
export function useIsCheckingScreenshotInBackground(): boolean {
  const isOcrEnabled = useFlag('payments_ocr_mv_code_extraction');
  const isVerifying =
    useIsMutating({ mutationKey: SCREENSHOT_VERIFICATION_KEY }) > 0;

  return isOcrEnabled && isVerifying;
}

/**
 * Handles a transaction screenshot uploaded by a carrier who can't find the MV code.
 *
 * With `payments_ocr_mv_code_extraction` on, the code is read from the
 * screenshot in the browser and submitted to Moov, so the bank account is
 * verified without a manual review. If no single code is found, or Moov
 * rejects it, the screenshot is uploaded for manual review as before.
 *
 * The OCR attempt is not registered as a verification error: it shouldn't use
 * up one of the carrier's own attempts.
 *
 * The carrier may close the drawer while the check runs. The mutation keeps
 * running, and its `onSuccess`/`onError` below still fire (unlike callbacks
 * passed to `mutate`), so the result is always shown and saved.
 */
export function useVerifyWithScreenshot() {
  const isOcrEnabled = useFlag('payments_ocr_mv_code_extraction');
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbarStack();
  const { invalidateCarrierSettings } = useCarrierSettingsCache();
  const { mutateAsync: completeInstantVerification } =
    useCompleteInstantBankAccountVerification();
  const { mutateAsync: uploadScreenshot } = useUploadMicroDepositScreenshot();
  const { refetch: refetchSuperPaySettings } = useSuperPaySettings();

  async function isBankAccountVerified(): Promise<boolean> {
    const { data } = await refetchSuperPaySettings();
    return data?.bank_accounts?.[0]?.verification_status === 'verified';
  }

  async function verifyWithCode(code: string): Promise<AutoVerification> {
    try {
      await completeInstantVerification(code);
      return 'verified';
    } catch (error) {
      // A wrong code is a normal outcome here; anything else (e.g. our sync
      // call failing after Moov accepted the code) may still have verified it.
      const { isExceeded, isStatusError } = getStatusErrors({ payload: error });

      if (isExceeded) return 'rejected_exceeded';
      if (isStatusError) return 'rejected_incorrect';

      return (await isBankAccountVerified()) ? 'verified' : 'error';
    }
  }

  return useMutation({
    mutationKey: SCREENSHOT_VERIFICATION_KEY,
    mutationFn: async ({
      file,
    }: ScreenshotVerificationVariables): Promise<ScreenshotVerificationResult> => {
      const startedAt = performance.now();
      const ocr = isOcrEnabled ? await readMvCodeFromImage(file) : null;
      const autoVerification =
        ocr?.result === 'found'
          ? await verifyWithCode(ocr.code)
          : 'not_attempted';

      if (autoVerification !== 'verified') {
        await uploadScreenshot(file);
      }

      return {
        outcome:
          autoVerification === 'verified' ? 'verified' : 'submitted_for_review',
        autoVerification,
        ocr,
        durationMs: Math.round(performance.now() - startedAt),
      };
    },
    onSuccess: (
      { outcome, autoVerification, ocr, durationMs },
      { onVerified },
    ) => {
      void queryClient.invalidateQueries({ queryKey: ['superpay-settings'] });

      if (outcome === 'verified') {
        // Carrier settings drive the SuperPay verification banners.
        invalidateCarrierSettings();
        onVerified?.();
      }

      addSnackbar(
        outcome === 'verified'
          ? 'Bank account verified'
          : isOcrEnabled
            ? 'Screenshot submitted for bank verification.'
            : 'Screenshot submitted for review',
        { variant: 'success' },
      );

      trackSuperPayEvent({
        name: 'Carrier Submitted Transaction Screenshot',
        outcome,
        auto_verification: autoVerification,
        ocr_result: ocr?.result ?? 'off',
        ocr_duration_ms: ocr?.durationMs,
        total_duration_ms: durationMs,
      });
    },
    onError: (error) => {
      addSnackbar(
        isOcrEnabled
          ? 'We couldn’t submit your screenshot. Please try again.'
          : error.message,
        { variant: 'error' },
      );
    },
  });
}
